<!-- Laid out the way Payment Processing is — a card carrying the title, the controls and
     the actions, then a results card holding nothing but the result — with the two kinds of
     control kept apart: the header asks for a day's data, the row under it works on what
     came back. -->
<div class="payment-errors-container">

  <!-- Filter Card -->
  <mat-card class="content-card filter-card">
    <mat-card-content>

      <!-- Card Header: the request. The due date is not a filter — it changes nothing until
           Run scan is pressed — so it stands with the button that sends it, apart from the
           controls below that act on the result as they are typed. -->
      <div class="card-header">
        <h2 class="page-title">Payment Errors</h2>

        <div class="header-actions">
          <mat-form-field appearance="outline" class="scan-date" subscriptSizing="dynamic">
            <mat-label>Due date</mat-label>
            <input
              matInput
              [matDatepicker]="datePicker"
              [formControl]="scanDate"
              [min]="minScanDate"
              [max]="maxScanDate" />
            <mat-datepicker-toggle matIconSuffix [for]="datePicker"></mat-datepicker-toggle>
            <mat-datepicker #datePicker></mat-datepicker>
          </mat-form-field>

          <button
            mat-raised-button
            color="primary"
            type="button"
            [disabled]="isScanning"
            (click)="runScan()"
            matTooltip="Reads every KapCharge transaction created on this due date and compares it against our table">
            <mat-icon>sync</mat-icon>
            Run scan
          </button>
        </div>
      </div>

      <!-- Everything that works on the result the scan returned: narrow it, repair it, take
           it away. Disabled until there is one — see the component's setFiltersEnabled. -->
      <div class="toolbar-row">
        <div class="result-filters">
          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Loan number</mat-label>
            <mat-icon matPrefix>search</mat-icon>
            <input matInput [formControl]="loanSearch" placeholder="Matches our loan or the one KapCharge charged" />
          </mat-form-field>

          <mat-form-field appearance="outline" subscriptSizing="dynamic">
            <mat-label>Issue</mat-label>
            <mat-select [formControl]="issueFilter">
              <mat-option *ngFor="let option of issueOptions" [value]="option.value">
                {{ option.label }}
                <span class="option-count" *ngIf="option.value !== 'all' && issueCounts[option.value]">
                  ({{ issueCounts[option.value] }})
                </span>
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="toolbar-actions">
          <!-- One button: ids then statuses, in that order. The order is not optional — a
               status corrected before the id it depends on mirrors the wrong transaction's
               result onto the row — so the page does not offer a way to run half of it. -->
          <button
            mat-raised-button
            color="primary"
            type="button"
            [disabled]="!summary || isFixing || isScanning"
            (click)="confirmRepair()"
            matTooltip="Writes the correct transaction ids, then the statuses this scan read at KapCharge, posting an NSF into NLS where one applies. Any NSF that then needs reversing is listed at the end.">
            <mat-spinner *ngIf="isFixing" diameter="18" class="btn-spinner"></mat-spinner>
            <mat-icon *ngIf="!isFixing">build</mat-icon>
            Fix IDs and Statuses
          </button>

          <!-- Writes the filtered findings, so what the file holds is what the table shows. -->
          <button
            mat-stroked-button
            type="button"
            [disabled]="!canExport"
            (click)="exportCsv()"
            matTooltip="Downloads every row the current filters select, not just this page">
            <mat-icon>download</mat-icon>
            Export CSV
          </button>
        </div>
      </div>
    </mat-card-content>
  </mat-card>

  <!-- Results Card -->
  <mat-card class="content-card list-card">
    <mat-card-content>

      <!-- What the scan says about itself, above what it found. -->
      <div class="summary" *ngIf="summary && !isScanning">
        <span class="summary__item">
          {{ untrackedCount | number }} untracked at KapCharge
        </span>
        <span class="summary__item summary__item--timing">
          <mat-icon>schedule</mat-icon>
          {{ formatDuration(summary.durationMs) }}
          <span class="muted">· {{ summary.apiCalls | number }} API call{{ summary.apiCalls === 1 ? '' : 's' }}</span>
        </span>
      </div>

      <!-- A scan that could not read everything must never read as a clean bill of health. -->
      <div class="scan-warning" *ngIf="scanIncomplete && summary as last">
        <mat-icon>warning</mat-icon>
        <span>
          This scan was incomplete, so findings below may be wrong in either direction.
          <ng-container *ngIf="last.truncatedDetail">
            Paging was cut short for: {{ last.truncatedDetail }}.
          </ng-container>
          Re-run it.
        </span>
      </div>

      <!-- Only what the run did NOT finish. The counts of what it did are gone: they said
           nothing an operator acts on, and the findings below already show what is left. -->
      <div class="fix-report" *ngIf="fixReport as report">
        <p class="fix-report__line fix-report__line--skipped" *ngFor="let item of report.skipped">
          <mat-icon>report_problem</mat-icon>
          Row {{ item.transactionId }} (loan {{ item.loanNumber }}) left alone: {{ item.reason }}
        </p>
      </div>

      <div class="fix-report" *ngIf="statusReport as report">
        <p class="fix-report__line fix-report__line--skipped"
           *ngFor="let item of statusRowsWorthShowing(report)">
          <mat-icon>report_problem</mat-icon>
          <span>
            Loan {{ item.loanNumber }} (txn {{ item.kapchargeTransactionId }}):
            <ng-container [ngSwitch]="item.outcome">
              <ng-container *ngSwitchCase="'ignored'">
                left at {{ item.status }} — already final, so KapCharge's {{ item.kapchargeStatus }} was not written
              </ng-container>
              <ng-container *ngSwitchCase="'unknown-status'">
                the scan read a status we could not compare; nothing written
              </ng-container>
              <ng-container *ngSwitchDefault>
                the write failed; nothing changed on this row
              </ng-container>
            </ng-container>
          </span>
        </p>
      </div>

      <div class="table-skeleton" *ngIf="isScanning">
        <div class="table-skeleton__row" *ngFor="let row of skeletonRows">
          <ngx-skeleton-loader count="2" appearance="line"
            [theme]="{ height: '14px', width: '180px', margin: '0 0 6px 0' }"></ngx-skeleton-loader>
          <ngx-skeleton-loader count="2" appearance="line"
            [theme]="{ height: '14px', width: '100%', margin: '0 0 6px 0' }"></ngx-skeleton-loader>
        </div>
      </div>

      <table mat-table [dataSource]="findings" [trackBy]="trackById" class="flat-table" *ngIf="!isScanning">
        <!-- The reference side: loan, due date and amount are taken as correct, so they
             identify the payment rather than being part of the comparison. -->
        <ng-container matColumnDef="payment">
          <th mat-header-cell *matHeaderCellDef>Payment</th>
          <td mat-cell *matCellDef="let row">
            <div class="stacked-cell">
              <span class="mono">
                {{ row.loanNumber || row.remoteLoanNumber || '—' }}
                <ng-container *ngIf="row.amountInCents !== null">
                  · {{ formatCents(row.amountInCents) }}
                </ng-container>
              </span>
              <span class="customer-name" *ngIf="row.customerName">{{ row.customerName }}</span>
              <span class="muted" *ngIf="row.dueDate">due {{ row.dueDate }}</span>
              <span class="muted" *ngIf="row.localStatus">{{ row.localStatus }}</span>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="errors">
          <th mat-header-cell *matHeaderCellDef>Errors</th>
          <td mat-cell *matCellDef="let row" class="errors-cell">
            <!-- Nothing of ours to compare against: show what KapCharge holds. -->
            <div class="diff" *ngIf="isRemoteOnly(row); else localSide">
              <div class="diff__row diff__row--single">
                <span class="diff__label">At KapCharge</span>
                <span class="diff__value diff__value--theirs">
                  {{ row.kapchargeTransactionId }}
                  <ng-container *ngIf="row.remoteAmountInCents !== null">
                    · {{ formatCents(row.remoteAmountInCents) }}
                  </ng-container>
                  <ng-container *ngIf="row.remoteStatus"> · {{ row.remoteStatus }}</ng-container>
                </span>
              </div>
              <div class="diff__row diff__row--single">
                <span class="diff__label">On our side</span>
                <span class="diff__value diff__value--missing">no record</span>
              </div>
              <p class="diff__note">{{ row.detail }}</p>
            </div>

            <ng-template #localSide>
              <!-- Matched, and these are the fields that disagree. -->
              <div class="diff" *ngIf="differences(row).length > 0; else noDiff">
                <div class="diff__row diff__head">
                  <span class="diff__label"></span>
                  <span class="diff__value">Ours</span>
                  <span class="diff__value">KapCharge</span>
                </div>
                <div class="diff__row" *ngFor="let d of differences(row)">
                  <span class="diff__label">{{ d.label }}</span>
                  <span class="diff__value diff__value--ours">{{ d.ours }}</span>
                  <span class="diff__value diff__value--theirs">{{ d.theirs }}</span>
                </div>
              </div>

              <!-- No counterpart found, or a finding that is not a field comparison
                   (a duplicated id, an ambiguous match) — those need the explanation. -->
              <ng-template #noDiff>
                <p class="diff__note">{{ row.detail }}</p>
              </ng-template>
            </ng-template>

            <!-- Why the submit failed, straight from the row. An Error row reads as "not
                 found at KapCharge" — correct, and useless without the reason it never got
                 there. Only present when the row is actually in Error. -->
            <p class="local-error" *ngIf="row.localError">
              <mat-icon>error_outline</mat-icon>
              <span>{{ row.localError }}</span>
            </p>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
      </table>

      <div *ngIf="!isScanning && findings.length === 0" class="empty-state">
        {{ emptyStateMessage }}
      </div>

      <mat-paginator
        [length]="rowCount"
        [pageSize]="pageSize"
        [pageIndex]="pageIndex"
        [pageSizeOptions]="pageSizeOptions"
        (page)="onPage($event)"
        showFirstLastButtons>
      </mat-paginator>
    </mat-card-content>
  </mat-card>
</div>
